import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { ToasterState } from 'src/AppState/ToasterState';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { Resource } from 'src_common/common/mobx-utils/Resource';

const CUSTOMER_ACCOUNT_TYPE = 'customer';
const DEFAULT_SESSION_DURATION_SECONDS = 21600;
const SECONDS_PER_HOUR = 3600;
const MAX_HOURS = 8;

const validatePositiveHours = (value: string): Result<number> => {
    if (value.trim() === '') {
        return Result.createError('This field is required.');
    }

    if (value.includes(',')) {
        return Result.createError('Wrong number format');
    }

    const trimmedValue = value.trim();
    if (!/^\d+(\.\d+)?$/.test(trimmedValue)) {
        return Result.createError('Number expected');
    }

    const hours = parseFloat(trimmedValue);
    if (isNaN(hours)) {
        return Result.createError('Number expected');
    }

    if (hours <= 0) {
        return Result.createError('Duration must be greater than zero');
    }

    if (hours > MAX_HOURS) {
        return Result.createError(`Value must be at most ${MAX_HOURS} hours`);
    }

    return Result.createOk(hours);
};

const secondsToHoursValue = (seconds: number): string => {
    const hours = seconds / SECONDS_PER_HOUR;
    return String(hours);
};

export class AutoLogoutState {
    private readonly configResource: Resource<number>;
    private readonly disposers: Array<() => void> = [];

    @observable public isSaving: boolean = false;
    @observable public errorMessage: string | null = null;
    @observable public originalHoursValue: string = '';

    public readonly durationHours: FormInputState<string, number>;

    public constructor(private readonly appState: AppState) {
        this.durationHours = FormInputState.new('').map(validatePositiveHours);

        this.configResource = new Resource(async () => {
            const response = await this.appState.common.trpcClient.client.accountsConfig.getAccountsConfig.query();

            if (response.type === 'error') {
                throw new Error(response.message);
            }

            const customerConfig = response.data.accountTypes[CUSTOMER_ACCOUNT_TYPE];
            const sessionExpirationTime = customerConfig?.sessionExpirationTime;

            if (typeof sessionExpirationTime === 'number' && sessionExpirationTime > 0) {
                return sessionExpirationTime;
            }

            return DEFAULT_SESSION_DURATION_SECONDS;
        });

        makeObservable(this);

        this.disposers.push(
            reaction(
                () => this.sessionExpirationSeconds,
                (seconds) => {
                    if (seconds !== null) {
                        this.loadDurationToForm(seconds);
                    }
                },
                { fireImmediately: true }
            )
        );
    }

    public dispose(): void {
        this.disposers.forEach((dispose) => dispose());
    }

    @computed public get isLoading(): boolean {
        return this.configResource.get().type === 'loading';
    }

    @computed public get sessionExpirationSeconds(): number | null {
        const result = this.configResource.get();

        if (result.type === 'ready') {
            return result.value;
        }

        if (result.type === 'error') {
            this.errorMessage = result.message;
        }

        return null;
    }

    @action private loadDurationToForm = (seconds: number): void => {
        const hoursValue = secondsToHoursValue(seconds);
        this.durationHours.setValue(hoursValue);
        this.originalHoursValue = hoursValue;
    };

    @action private setError = (message: string | null): void => {
        this.errorMessage = message;
    };

    @action private setSaving = (saving: boolean): void => {
        this.isSaving = saving;
    };

    public async refresh(): Promise<void> {
        await this.configResource.refresh();
    }

    @action public updateConfig = async (): Promise<void> => {
        this.durationHours.setAsVisited();

        if (this.durationHours.result.value.type === 'error') {
            this.setError('Please fix all validation errors');
            return;
        }

        const hours = this.durationHours.result.value.data;
        const durationSeconds = Math.round(hours * SECONDS_PER_HOUR);
        const maxDurationSeconds = MAX_HOURS * SECONDS_PER_HOUR;

        if (durationSeconds <= 0 || durationSeconds > maxDurationSeconds) {
            this.setError(`Duration must be greater than zero and at most ${MAX_HOURS} hours`);
            return;
        }

        try {
            this.setSaving(true);
            this.setError(null);

            const response =
                await this.appState.common.trpcClient.client.accountsConfig.updateAccountTypeSessionDuration.mutate({
                    accountType: CUSTOMER_ACCOUNT_TYPE,
                    durationSeconds,
                });

            if (response.type === 'error') {
                this.setError(response.message);
                return;
            }

            ToasterState.get(this.appState.common).setToast({
                variant: 'success',
                title: 'Success',
                message: 'Auto logout configuration saved.',
            });

            await this.refresh();
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to update auto logout configuration');
        } finally {
            this.setSaving(false);
        }
    };
}
